/**
 * Losi App Preview Marker
 *
 * Parses safe chat markers that hand off app ideas to the Apps preview flow.
 *
 * Primary responsibilities:
 * - Accept only prompt-level App preview requests from chat
 * - Prevent chat output from becoming a trusted app-definition source
 *
 * Dependencies:
 * - None
 */

export const LOSI_APP_PREVIEW_MARKER_REGEX =
  /\\?\[LOSI_APP_PREVIEW\\?\]([\s\S]*?)\\?\[\/LOSI_APP_PREVIEW\\?\]/g

export const LOSI_APP_PREVIEW_TEMPLATE_IDS = [
  'website_improvement',
  'ai_readiness_audit',
  'content_campaign',
  'sales_follow_up',
  'client_onboarding',
  'bug_triage',
  'meeting_to_execution',
  'social_repurposing',
  'research_tracker',
  'proposal_builder'
] as const

export type LosiAppPreviewTemplateId = (typeof LOSI_APP_PREVIEW_TEMPLATE_IDS)[number]

export interface LosiAppPreviewMarkerPayload {
  description?: string
  prompt: string
  templateId?: LosiAppPreviewTemplateId
  title?: string
}

export interface LosiAppPreviewMarkerMatch {
  payload: LosiAppPreviewMarkerPayload
  rawText: string
}

const TEMPLATE_ID_SET = new Set<string>(LOSI_APP_PREVIEW_TEMPLATE_IDS)
const MAX_PROMPT_LENGTH = 2000
const MAX_TITLE_LENGTH = 120
const MAX_DESCRIPTION_LENGTH = 280

// --------------

export function parseLosiAppPreviewMarkerPayload(
  payloadText: string
): LosiAppPreviewMarkerPayload | null {
  const parsed = parseJsonObject(stripJsonFence(payloadText))
  if (!parsed) return null

  const prompt = readTextField(parsed, 'prompt', MAX_PROMPT_LENGTH)
  if (!prompt) return null

  const title = readTextField(parsed, 'title', MAX_TITLE_LENGTH)
  const description = readTextField(parsed, 'description', MAX_DESCRIPTION_LENGTH)
  const templateId = readTemplateId(parsed.templateId)

  return {
    ...(description ? { description } : {}),
    prompt,
    ...(templateId ? { templateId } : {}),
    ...(title ? { title } : {})
  }
}

// --------------

export function findLosiAppPreviewMarkers(content: string): LosiAppPreviewMarkerMatch[] {
  return Array.from(content.matchAll(LOSI_APP_PREVIEW_MARKER_REGEX)).flatMap((match) => {
    const payload = parseLosiAppPreviewMarkerPayload(match[1] ?? '')
    if (!payload) return []

    return [{ payload, rawText: match[0] }]
  })
}

// --------------

export function serializeLosiAppPreviewMarker(input: LosiAppPreviewMarkerPayload): string {
  const payload = parseLosiAppPreviewMarkerPayload(JSON.stringify(input))
  if (!payload) {
    throw new Error('A Losi App preview marker needs a prompt')
  }

  return `[LOSI_APP_PREVIEW]${JSON.stringify(payload)}[/LOSI_APP_PREVIEW]`
}

// --------------

function stripJsonFence(value: string) {
  const trimmed = value.trim()
  const match = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)

  return (match?.[1] ?? trimmed).trim()
}

// --------------

function parseJsonObject(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value) as unknown
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

// --------------

function readTextField(record: Record<string, unknown>, key: string, maxLength: number) {
  const value = record[key]
  if (typeof value !== 'string') return null

  const cleaned = value
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!cleaned) return null

  return cleaned.slice(0, maxLength)
}

// --------------

function readTemplateId(value: unknown): LosiAppPreviewTemplateId | null {
  if (typeof value !== 'string') return null

  return TEMPLATE_ID_SET.has(value) ? (value as LosiAppPreviewTemplateId) : null
}
